import React, { useState, useEffect, useRef } from 'react'
import moment from 'moment'
import { Fragment } from 'react'
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faXmarkCircle,
  faUpload,
  faDownload,
  faCircleXmark,
} from '@fortawesome/free-solid-svg-icons'
import useAxiosPrivateSAI from '../../../../hooks/useAxiosPrivateSAI'
import useAuth from '../../../../hooks/useAuth'
import { useTheme } from '../../../../context/ThemeProvider'
import {toast} from 'sonner'
import { ImageConfig } from './ImageConfig'

const UploadStaffSlideOut = ({ openCsv, setOpenCsv, policyData, onUploaded  }) => {
  const theme = useTheme()
  const { setShowLoader } = useAuth()
  const style = {
    backgroundColor: theme.backgroundColor,
    color: theme.textColor,
  }
  const [errorMsg, setErrorMsg] = useState('')
  const [isSuccess, setIsSuccess] = useState(false)
  const wrapperRef = useRef(null)
  const [fileList, setFileList] = useState([])
  const [showFiles, setShowFiles] = useState([])
  const [commAttachments, setCommAttachments] = useState([])
  const [selectedFile, setSelectedFile] = useState()
  const axios = useAxiosPrivateSAI()
  const id = policyData?.policy_id

  const onDragEnter = () => wrapperRef.current.classList.add('dragover')
  const onDragLeave = () => wrapperRef.current.classList.remove('dragover')
  const onDragOver = (e) => {
    e.stopPropagation()
    e.preventDefault()
  }
  const onDrop = (e) => {
    e.preventDefault()
    wrapperRef.current.classList.remove('dragover')
    onFileDrop(e.nativeEvent.dataTransfer.files)
  }

  const handleClose = () => {
    setOpenCsv(false)
    fileRemove()
  }

  const allowedFileTypes = [
    '.doc',
    '.docx',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    '.xls',
    '.xlsx',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/csv',
    '.csv',
  ]

  const isValidFileType = (file) => {
    const extension = `.${file.name.split('.').pop()}`
    const mimeType = file.type
    return (
      allowedFileTypes.includes(extension) ||
      allowedFileTypes.includes(mimeType)
    )
  }

  const onFileUpload = async (e) => {
    e.preventDefault()
    const newFile = e.target.files[0]

    console.log(newFile);
    if (!newFile.name.toLowerCase().includes('staff_import_template')) {
      fileRemove(newFile)
      return toast.error('Please download the xlsx template and use this to upload your staff.')
    }

    if (newFile && isValidFileType(newFile)) {
      const updatedList = [...fileList, newFile]
      setFileList(updatedList)
    } else {
      toast.error('Invalid file type. Please select a valid file.')
    }
  }

  const onFileDrop = async (files) => {
    const newFile = files[0]

    console.log(newFile);
    if (!newFile.name.toLowerCase().includes('staff_import_template')) {
      fileRemove(newFile)
      return toast.error('Please download the xlsx template and use this to upload your staff.')
    }

    if (newFile && isValidFileType(newFile)) {
      const updatedList = [...fileList, newFile]
      setFileList(updatedList)
    } else {
      toast.error('error', 'Invalid file type')
      return
    }
  }
  
  const downloadTemplate = async () => {
    try {
      const response = await axios.get(`/api/get/new_import_template/${policyData?.policy_id}`, {
        responseType: 'blob',
      })

      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'staff_import_template.xlsx')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading template:', error)
    }
  }
  const uploadDocuments = async () => {  
    setShowLoader(true)
  
    try {
      const file = fileList[0]
      const policyData = JSON.parse(localStorage.getItem('policyData') || '{}')
      const orgType    = JSON.parse(localStorage.getItem('orgType') || '{}')
  
      const policyId = policyData?.policy_id
      const orgId    = orgType?.id
  
      if (!policyId || !orgId) {
        toast.error('Missing policy or organisation context')
        return
      }
  
      const csrf =
        document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        || window._csrf_token
        || '';
  
      const fd = new FormData()
      fd.append('file', file, file.name)
      fd.append('policyId', policyId)
      fd.append('orgid', orgId)
      fd.append('appendReplace', 'append')
      if (csrf) fd.append('_csrf_token', csrf)
      const res = await axios.post('/api/staffs/csvupload', fd)
      const warnings = res?.data?.warnings ?? []
      const results  = res?.data?.results ?? []
      const rows = res?.data?.results ?? []
      onUploaded?.(rows)
      setFileList([])

      warnings.forEach(w => toast.warning(w))
      toast.success(`Uploaded ${results.length} record(s)`)
      setFileList([])

    } catch (error) {
      const msg = error?.response?.data?.message || 'Upload failed'
      toast.error(msg)
      console.error(error)
    } finally {
      setShowLoader(false)
    }
    
    setOpenCsv(false)
  }  

  const fileRemove = (file) => {
    console.log(file)
    const updatedList = [...fileList]
    updatedList.splice(fileList.indexOf(file), 1)
    setFileList(updatedList)
  }

  const deleteFile = async (file) => {
    setShowLoader(true)
    try {
      const response = await axios.get(`/api/clients/delete-document/${file.id}`)

      if (!response.data.error) {
        const updatedList = [...showFiles]
        updatedList.splice(showFiles.indexOf(file), 1)
        setShowFiles(updatedList)

        toast.success('Document removed')
      } else {
        toast.error('Could not remove document')
      }
    } catch (error) {
      toast.error('Could not remove document')
    }
    setShowLoader(false)
  }

  const deleteAttachment = async (file) => {
    setShowLoader(true)
    try {
      const response = await axios.get(
        `/api/clients/${id}/delete-attachment/${file.id}`
      )

      if (!response.data.error) {
        const updatedList = [...commAttachments]
        updatedList.splice(commAttachments.indexOf(file), 1)
        setCommAttachments(updatedList)

        toast.success('Document removed')
      } else {
        toast.error('Could not remove document')
      }
    } catch (error) {
      toast.error('Could not remove document')
    }
    setShowLoader(false)
  }

  return (
    <Dialog open={openCsv} onClose={handleClose} className="relative z-[100]">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition duration-500 ease-in-out data-[closed]:opacity-0"
      />
      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
            <DialogPanel
              transition
              className="pointer-events-auto w-screen max-w-2xl transform transition duration-500 ease-in-out data-[closed]:translate-x-full sm:duration-700"
            >
              <div className="flex h-full flex-col overflow-y-scroll bg-white shadow-xl">
                <div className=" px-4 py-6 sm:px-6"  style={{ backgroundColor: style.backgroundColor }}>
                  <div className="flex items-center justify-between">
                    <div>
                      <DialogTitle className="text-lg font-semibold leading-6 text-white">
                        Upload your Staff List
                      </DialogTitle>
                      <div className="text-xs text-white">
                        Please read the instruction guide before uploading your staff list.
                      </div>
                    </div>
                    <div className="ml-3 flex h-7 items-center">
                      <button
                        type="button"
                        className="relative rounded-md text-white  focus:outline-none focus:ring-2 focus:ring-white"
                        onClick={() => {
                          setOpenCsv(false)
                          fileRemove()
                        }}
                      >
                        <span className="absolute -inset-2.5" />
                        <span className="sr-only">Close panel</span>
                        <FontAwesomeIcon  className="h-6 w-6" icon={faXmarkCircle} />
                      </button>
                    </div>
                  </div>
                </div>
                <div className='px-6 py-2 text-sas-dark'>
                  <div>Please follow the instructions below to correctly import your staff:</div>
                  <ol className='list-decimal pl-10 text-sm'>
                    <li>Download the template from the "Download CSV Template" button.</li>
                    <li>Do not amend or change the column names.</li>
                    <li>You can add new staff members by adding a new row to the bottom of the table.</li>
                    <li>When adding new staff members set the ID column value to 0. Then you can add their details.</li>
                    <li>When updating a staff member do not change the ID column or copy & past to a new row. You can update their details on the existing row.</li>
                    <li>Please only upload one csv file at a time.</li>
                    <li>
                      Note: Only records with a Forename, Surname, and Date of Birth will be uploaded to the system.
                            <br />Include the Staff Type and FTE to automatically add them to the correct staff groups.
                            The staff type can be either its full name (check the spelling) or its corresponding number.
                            Once you are happy with the data provided select 'Upload Staff' below.
                            <br/> Dates of Birth must be in the 'DD/MM/YYYY' or 'YYYY-MM-DD' format.
                    </li>
                  </ol>
                </div>

                <div className="col-span-full">
                  <>
                    {/* Drag and Drop Upload */}
                    <label htmlFor="file-upload"
                      ref={wrapperRef}
                      onDragEnter={onDragEnter}
                      onDragLeave={onDragLeave}
                      onDrop={onDrop}
                      onDragOver={onDragOver}
                      className="text-center p-3 cursor-pointer"
                      >
                      <div className="border border-dashed border-gray-300 rounded-md mx-6">
                        <img src="/images/laptop.png" alt="Upload" className="mx-auto h-44 mb-3" />
                        <p className="text-sas font-medium">Drag and Drop or click to upload a file</p>
                        <p className="text-sm text-gray-500 pb-6">CSV or XLS</p>
                      </div>
                      <input
                        type="file"
                        id="file-upload"
                        name="file-upload"
                        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        onChange={onFileUpload}
                        className="hidden"
                      />
                    </label>

                    <div className="grid grid-cols-3 gap-3 mb-16">
                      {fileList.map((item, index) => (
                        <div
                          key={index}
                          className="drop-file-preview__item flex items-center justify-between "
                        >
                          <div className="flex items-center">
                            <img
                              src={
                                ImageConfig[item.type.split('/')[1]] ||
                                ImageConfig['default']
                              }
                              alt=""
                              className="w-12"
                            />

                            <div className="drop-file-preview__item__info">
                              <p className="break-words">{item.name}</p>
                              <p className='text-sm'>{item.size}</p>
                            </div>
                          </div>

                          <span
                            className=" hover:text-red-700 drop-file-preview__item__del"
                            onClick={() => fileRemove(item)}
                          >
                            <FontAwesomeIcon
                              icon={faCircleXmark}
                              className=" text-gray-700 hover:text-red-700 w-6 h-6"
                            />
                          </span>
                        </div>
                      ))}
                    </div>
                    {/* CSV Template Download */}
                    <div className="px-6 flex items-center justify-between absolute bottom-2 w-full">
                      <button onClick={uploadDocuments} className="inline-flex items-center border border-sas bg-white text-sas rounded-md overflow-hidden text-sm font-medium">
                        <span className="px-4 py-2">Upload CSV</span>
                        <span className="bg-sas text-white px-3 py-2">
                          <FontAwesomeIcon icon={faUpload} />
                        </span>
                      </button>
                      
                      <button type="button" onClick={downloadTemplate}  className="inline-flex items-center border border-sas text-sas bg-white rounded-md overflow-hidden text-sm font-medium">
                        <span className="px-4 py-2">Download CSV Template</span>
                        <span className="bg-sas text-white px-3 py-2">
                          <FontAwesomeIcon icon={faDownload} />
                        </span>
                      </button>
                    </div>
                  </>
                </div>
                
              </div>
            </DialogPanel>
          </div>
        </div>
      </div>
    </Dialog>
  )
}

export default UploadStaffSlideOut
